/**
 * Parse a spend-cube workbook (Raw Data sheet) and replace one company's lines.
 * The supplier-name map is kept across uploads.
 */
import * as XLSX from 'xlsx'

export type SpendLineInsert = {
  company_id: string
  supplier_id: string | null
  parent_name: string
  material_group: string | null
  plant_location: string | null
  po_number: string | null
  po_line: string | null
  sap_item: string | null
  ordered_amount: number | null
  invoice_date: string | null
  invoice_year: number | null
  spend: number
  cost_center: string | null
  department: string | null
  report_rollup: string | null
  rollup_2: string | null
  account: string | null
  account_name: string | null
  account_rollup: string | null
  oracle_code: string | null
  contractor: boolean
  currency: string | null
  status: string | null
  description: string | null
  procurement_bu: string | null
}

export type SpendBudgetInsert = {
  invoice_year: number
  quarter: number
  budget: number
}

export type SpendPoLineInsert = {
  parent_name: string
  po_number: string | null
  line_status: string | null
  contractor: boolean
  invoice_spend: number
  commitment: number
}

export type SpendImportSummary = {
  lines: number
  mapped: number
  unmapped: number
  mapRowsKept: number
  budgetQuarters: number
  poLines: number
  errors: string[]
}

type ParsedLine = Omit<SpendLineInsert, 'company_id' | 'supplier_id'>

const CHUNK = 300

function normHeader(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function normName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

function cellText(value: unknown): string | null {
  if (value == null || value === '') return null
  const text = String(value).trim()
  return text || null
}

function cellNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const n = Number(String(value).replace(/[$,\s]/g, ''))
  return Number.isFinite(n) ? n : null
}

function cellBool(value: unknown): boolean {
  const t = String(value ?? '').trim().toLowerCase()
  return t === 'y' || t === 'yes' || t === 'true' || t === '1' || t === 'consultant' || t === 'contractor'
}

function excelDate(value: unknown): string | null {
  if (value == null || value === '') return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }
  if (typeof value === 'number' && value > 20000 && value < 80000) {
    const utc = new Date(Date.UTC(1899, 11, 30) + Math.round(value) * 86400000)
    if (!Number.isNaN(utc.getTime())) return utc.toISOString().slice(0, 10)
  }
  const text = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10)
  const asDate = new Date(text)
  if (!Number.isNaN(asDate.getTime()) && text.length >= 6) return asDate.toISOString().slice(0, 10)
  return null
}

function headerIndex(headers: string[], ...needles: string[]): number {
  return headers.findIndex((h) => needles.some((n) => h === n || h.includes(n)))
}

function rawHeader(value: unknown): string {
  return String(value ?? '').trim().toLowerCase()
}

function findRaw(headers: string[], pred: (header: string) => boolean): number {
  return headers.findIndex(pred)
}

export function parseSpendWorkbook(buffer: ArrayBuffer): { rows: ParsedLine[]; sheetName: string } {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: false })
  const sheetName =
    wb.SheetNames.find((n) => /raw\s*data/i.test(n)) ||
    wb.SheetNames.find((n) => n.toLowerCase().includes('raw')) ||
    wb.SheetNames[0]
  if (!sheetName) throw new Error('The workbook has no sheets.')
  const sheet = wb.Sheets[sheetName]
  const matrix = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
    header: 1,
    defval: null,
    raw: true,
  })
  const headerRow = matrix.findIndex((row) =>
    row.some((cell) => normHeader(cell).includes('parent'))
  )
  if (headerRow < 0) throw new Error('Could not find a header row with a parent-name column.')
  const headerCells = matrix[headerRow] || []
  const headers = headerCells.map(normHeader)
  const rawHeaders = headerCells.map(rawHeader)
  const parentIdx = headerIndex(headers, 'standardized parent name', 'parent name', 'parent')
  if (parentIdx < 0) throw new Error('The Raw Data sheet needs a Standardized Parent Name column.')
  const col = {
    po: headerIndex(headers, 'purchase order number', 'po number'),
    sap: headerIndex(headers, 'sap item'),
    material: headerIndex(headers, 'material group'),
    plant: headerIndex(headers, 'plant location', 'plant'),
    poLine: headerIndex(headers, 'purchase order line', 'po line'),
    ordered:
      findRaw(rawHeaders, (h) => h.includes('ordered amount') && h.includes('$')) >= 0
        ? findRaw(rawHeaders, (h) => h.includes('ordered amount') && h.includes('$'))
        : headerIndex(headers, 'ordered amount'),
    invoiceDate: headerIndex(headers, 'invoice date'),
    invoiceYear: headerIndex(headers, 'invoice year'),
    spend: headerIndex(headers, 'spend'),
    cost: headerIndex(headers, 'cost center'),
    department: headerIndex(headers, 'department'),
    report: headerIndex(headers, 'report rollup'),
    rollup2: headerIndex(headers, 'roll up 2', 'rollup 2'),
    account: findRaw(rawHeaders, (h) => h.includes('account number')) >= 0
      ? findRaw(rawHeaders, (h) => h.includes('account number'))
      : headerIndex(headers, 'account'),
    accountName: findRaw(rawHeaders, (h) => h.includes('account name')),
    accountRollup: findRaw(rawHeaders, (h) => h.includes('account rollup')),
    oracle: findRaw(rawHeaders, (h) => h.includes('oracle')),
    contractor: headerIndex(headers, 'contractor consultant', 'contractor', 'consultant'),
    bu: headerIndex(headers, 'procurement bu'),
    currency: headerIndex(headers, 'currency'),
    status: headerIndex(headers, 'status'),
    description:
      findRaw(rawHeaders, (h) => h === 'description') >= 0
        ? findRaw(rawHeaders, (h) => h === 'description')
        : headerIndex(headers, 'line description'),
  }
  const rows: ParsedLine[] = []
  for (const row of matrix.slice(headerRow + 1)) {
    const parent = cellText(row[parentIdx])
    if (!parent) continue
    const invoiceDate = col.invoiceDate >= 0 ? excelDate(row[col.invoiceDate]) : null
    let invoiceYear = col.invoiceYear >= 0 ? cellNumber(row[col.invoiceYear]) : null
    if (invoiceYear != null && invoiceYear > 1900 && invoiceYear < 2100) {
      invoiceYear = Math.trunc(invoiceYear)
    } else if (invoiceDate) {
      invoiceYear = Number(invoiceDate.slice(0, 4))
    } else {
      invoiceYear = null
    }
    rows.push({
      parent_name: parent,
      material_group: col.material >= 0 ? cellText(row[col.material]) : null,
      plant_location: col.plant >= 0 ? cellText(row[col.plant]) : null,
      po_number: col.po >= 0 ? cellText(row[col.po]) : null,
      po_line: col.poLine >= 0 ? cellText(row[col.poLine]) : null,
      sap_item: col.sap >= 0 ? cellText(row[col.sap]) : null,
      ordered_amount: col.ordered >= 0 ? cellNumber(row[col.ordered]) : null,
      invoice_date: invoiceDate,
      invoice_year: invoiceYear,
      spend: col.spend >= 0 ? cellNumber(row[col.spend]) ?? 0 : 0,
      cost_center: col.cost >= 0 ? cellText(row[col.cost]) : null,
      department: col.department >= 0 ? cellText(row[col.department]) : null,
      report_rollup: col.report >= 0 ? cellText(row[col.report]) : null,
      rollup_2: col.rollup2 >= 0 ? cellText(row[col.rollup2]) : null,
      account: col.account >= 0 ? cellText(row[col.account]) : null,
      account_name: col.accountName >= 0 ? cellText(row[col.accountName]) : null,
      account_rollup: col.accountRollup >= 0 ? cellText(row[col.accountRollup]) : null,
      oracle_code: col.oracle >= 0 ? cellText(row[col.oracle]) : null,
      contractor: col.contractor >= 0 ? cellBool(row[col.contractor]) : false,
      currency: col.currency >= 0 ? cellText(row[col.currency]) : null,
      status: col.status >= 0 ? cellText(row[col.status]) : null,
      description: col.description >= 0 ? cellText(row[col.description]) : null,
      procurement_bu: col.bu >= 0 ? cellText(row[col.bu]) : null,
    })
  }
  if (rows.length === 0) throw new Error('No spend lines were found under the header row.')
  return { rows, sheetName }
}

/** Quarterly budget, when the workbook has a budget sheet. Null means the sheet is absent. */
export function parseSpendBudget(buffer: ArrayBuffer): SpendBudgetInsert[] | null {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: false })
  const sheetName = wb.SheetNames.find((n) => /budget/i.test(n))
  if (!sheetName) return null
  const matrix = XLSX.utils.sheet_to_json<(string | number | null)[]>(wb.Sheets[sheetName], {
    header: 1,
    defval: null,
    raw: true,
  })
  const headerRow = matrix.findIndex((row) => {
    const headers = row.map((cell) => normHeader(cell))
    return headers.some((h) => h.includes('budget')) && headers.some((h) => h.includes('quarter') || h.includes('qtr'))
  })
  if (headerRow < 0) return []
  const headers = (matrix[headerRow] || []).map(normHeader)
  const yearIdx = headerIndex(headers, 'year')
  const quarterIdx = headerIndex(headers, 'quarter', 'qtr')
  const budgetIdx = headerIndex(headers, 'budget')
  if (yearIdx < 0 || quarterIdx < 0 || budgetIdx < 0) return []
  const rows: SpendBudgetInsert[] = []
  for (const row of matrix.slice(headerRow + 1)) {
    const year = cellNumber(row[yearIdx])
    const quarterText = String(row[quarterIdx] ?? '')
    const quarterMatch = quarterText.match(/([1-4])/)
    const budget = cellNumber(row[budgetIdx])
    if (year == null || year < 1900 || year > 2100 || !quarterMatch || budget == null) continue
    rows.push({ invoice_year: Math.trunc(year), quarter: Number(quarterMatch[1]), budget })
  }
  return rows
}

/**
 * First block of the PO vs Invoice sheet: one row per PO line.
 * Stops at the first blank supplier so the later supplier pivot is not imported.
 * Null means that sheet is absent.
 */
export function parseSpendPoLines(buffer: ArrayBuffer): SpendPoLineInsert[] | null {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: false })
  const sheetName = wb.SheetNames.find((n) => /po\s*vs\s*invoice/i.test(n))
  if (!sheetName) return null
  const matrix = XLSX.utils.sheet_to_json<(string | number | null)[]>(wb.Sheets[sheetName], {
    header: 1,
    defval: null,
    raw: true,
  })
  const headerRow = matrix.findIndex((row) => {
    const headers = row.map((cell) => normHeader(cell))
    return headers.some((h) => h.includes('supplier')) && headers.some((h) => h.includes('commitment'))
  })
  if (headerRow < 0) return []
  const headers = (matrix[headerRow] || []).map(normHeader)
  const supplierIdx = headerIndex(headers, 'supplier name', 'supplier')
  const poIdx = headerIndex(headers, 'purchase order number', 'po number')
  const statusIdx = headerIndex(headers, 'line status', 'status')
  const contractorIdx = headerIndex(headers, 'contractor')
  const invoiceIdx = headers.findIndex((h) => h.includes('invoice') && h.includes('spend'))
  const commitmentIdx = headers.findIndex((h) => h.includes('commitment'))
  if (supplierIdx < 0 || commitmentIdx < 0) return []
  const rows: SpendPoLineInsert[] = []
  for (const row of matrix.slice(headerRow + 1)) {
    const parent = cellText(row[supplierIdx])
    if (!parent) break
    rows.push({
      parent_name: parent,
      po_number: poIdx >= 0 ? cellText(row[poIdx]) : null,
      line_status: statusIdx >= 0 ? cellText(row[statusIdx]) : null,
      contractor: contractorIdx >= 0 ? cellBool(row[contractorIdx]) : false,
      invoice_spend: invoiceIdx >= 0 ? cellNumber(row[invoiceIdx]) ?? 0 : 0,
      commitment: cellNumber(row[commitmentIdx]) ?? 0,
    })
  }
  return rows
}

type SupplierPick = { id: string; score: number | null }

async function loadSupplierIndex(
  supabase: Awaited<typeof import('../services/supabase')>['supabase'],
  companyId: string
): Promise<{
  byName: Map<string, SupplierPick>
  errors: string[]
}> {
  const errors: string[] = []
  const byName = new Map<string, SupplierPick>()
  const assigned = new Set<string>()
  const visible = new Set<string>()

  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase
      .from('supplier_companies')
      .select('supplier_id, company_id')
      .range(from, from + 999)
    if (error) {
      errors.push(error.message)
      break
    }
    const page = data || []
    for (const row of page) {
      assigned.add(row.supplier_id)
      if (row.company_id === companyId) visible.add(row.supplier_id)
    }
    if (page.length < 1000) break
  }

  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase
      .from('suppliers')
      .select('id, name, parent_name, overall_score')
      .range(from, from + 999)
    if (error) {
      errors.push(error.message)
      break
    }
    const page = data || []
    for (const row of page) {
      const global = !assigned.has(row.id)
      if (!global && !visible.has(row.id)) continue
      const score = row.overall_score == null ? null : Number(row.overall_score)
      const consider = (label: string | null) => {
        const key = label ? normName(label) : ''
        if (!key) return
        const prev = byName.get(key)
        if (!prev || (score ?? -1) > (prev.score ?? -1)) byName.set(key, { id: row.id, score })
      }
      consider(row.name)
      consider(row.parent_name)
    }
    if (page.length < 1000) break
  }

  return { byName, errors }
}

export async function replaceCompanySpend(
  companyId: string,
  rows: ParsedLine[],
  budget: SpendBudgetInsert[] | null = null,
  poLines: SpendPoLineInsert[] | null = null
): Promise<SpendImportSummary> {
  const { supabase } = await import('../services/supabase')
  const errors: string[] = []
  const { data: mapRows, error: mapError } = await supabase
    .from('company_spend_supplier_map')
    .select('parent_name, supplier_id')
    .eq('company_id', companyId)
  if (mapError) errors.push(mapError.message)
  const map = new Map<string, string>()
  for (const row of mapRows || []) {
    map.set(normName(row.parent_name), row.supplier_id)
  }
  const mapRowsKept = map.size

  const { byName, errors: supplierErrors } = await loadSupplierIndex(supabase, companyId)
  errors.push(...supplierErrors)

  const freshMaps: { company_id: string; parent_name: string; supplier_id: string }[] = []
  let mapped = 0
  const payloads: SpendLineInsert[] = rows.map((row) => {
    const key = normName(row.parent_name)
    let supplierId = map.get(key) || null
    if (!supplierId) {
      const match = byName.get(key)
      if (match) {
        supplierId = match.id
        if (!map.has(key)) {
          map.set(key, supplierId)
          freshMaps.push({ company_id: companyId, parent_name: row.parent_name, supplier_id: supplierId })
        }
      }
    }
    if (supplierId) mapped += 1
    return { ...row, company_id: companyId, supplier_id: supplierId }
  })

  const { error: deleteError } = await supabase
    .from('company_spend_lines')
    .delete()
    .eq('company_id', companyId)
  if (deleteError) {
    return {
      lines: 0,
      mapped: 0,
      unmapped: rows.length,
      mapRowsKept,
      budgetQuarters: 0,
      poLines: 0,
      errors: [...errors, deleteError.message],
    }
  }

  let lines = 0
  for (let i = 0; i < payloads.length; i += CHUNK) {
    const chunk = payloads.slice(i, i + CHUNK)
    const { error } = await supabase.from('company_spend_lines').insert(chunk)
    if (error) errors.push(`Rows ${i + 1}–${i + chunk.length}: ${error.message}`)
    else lines += chunk.length
  }

  let budgetQuarters = 0
  if (budget) {
    const { error: budgetDeleteError } = await supabase
      .from('company_spend_budget')
      .delete()
      .eq('company_id', companyId)
    if (budgetDeleteError) errors.push(budgetDeleteError.message)
    else if (budget.length > 0) {
      const { error: budgetError } = await supabase.from('company_spend_budget').insert(
        budget.map((row) => ({ ...row, company_id: companyId }))
      )
      if (budgetError) errors.push(budgetError.message)
      else budgetQuarters = budget.length
    }
  }

  let loadedPoLines = 0
  if (poLines) {
    const { error: poDeleteError } = await supabase
      .from('company_spend_po_lines')
      .delete()
      .eq('company_id', companyId)
    if (poDeleteError) errors.push(poDeleteError.message)
    else {
      for (let i = 0; i < poLines.length; i += CHUNK) {
        const chunk = poLines.slice(i, i + CHUNK).map((row) => ({ ...row, company_id: companyId }))
        const { error } = await supabase.from('company_spend_po_lines').insert(chunk)
        if (error) errors.push(`PO rows ${i + 1}–${i + chunk.length}: ${error.message}`)
        else loadedPoLines += chunk.length
      }
    }
  }

  if (freshMaps.length > 0) {
    const { error } = await supabase.from('company_spend_supplier_map').upsert(freshMaps, {
      onConflict: 'company_id,parent_name_key',
      ignoreDuplicates: true,
    })
    if (error) errors.push(error.message)
  }

  return {
    lines,
    mapped,
    unmapped: payloads.length - mapped,
    mapRowsKept,
    budgetQuarters,
    poLines: loadedPoLines,
    errors,
  }
}
